/**
 * Shared cell renderers for the service tables. Mirrors the legacy
 * `createServiceColumns` intent (id link, product-group, UPPERCASE service name,
 * entity name, CS followers, customer link, status pill, role/direction chips,
 * active/renew cycle ranges, dates) on `@vnetwork/ui` primitives + `--vn-*` tokens.
 */
import type { ReactNode } from 'react';
import { Badge } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { TableLink } from '@/components/table-link';
import { EntityLink } from '@/components/entity-link';
import { formatDate } from '@/lib/format';
import { roleLabel, directionLabel } from '@/pages/contracts/contract-format';
import type { ServiceRow } from '@/api/services';
import { serviceStatusTone, serviceStatusLabel } from '../services-format';

const muted = { color: 'var(--vn-fg-muted)', fontStyle: 'italic' } as const;
const nowrap = { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

/** Service-id link → the service detail route (id-only; name shown in a separate column). */
export function serviceIdCell(row: ServiceRow): ReactNode {
  return (
    <TableLink to={`/services/${row.service_id}`} title={String(row.service_id)}>
      {row.service_id}
    </TableLink>
  );
}

/** UPPERCASE service name (plain text, truncated). */
export function serviceNameCell(value?: string | null): ReactNode {
  if (!value) return <span style={muted}>—</span>;
  return (
    <span style={{ ...nowrap, textTransform: 'uppercase' }} title={value}>
      {value}
    </span>
  );
}

/** Customer: `{id} – {company_name | customer_name}` on a single line, linked to customer detail. */
export function serviceCustomerCell(row: ServiceRow): ReactNode {
  if (!row.customer_id) return <span style={muted}>—</span>;
  return (
    <EntityLink
      to={`/customers/${row.customer_id}`}
      id={row.customer_id}
      name={row.company_name || row.customer_name}
    />
  );
}

/** Status → `StatusPill` with the service tone map. */
export function serviceStatusCell(status?: string): ReactNode {
  return <StatusPill tone={serviceStatusTone(status)}>{serviceStatusLabel(status)}</StatusPill>;
}

/** Contract role as a neutral `Badge`. */
export function roleCell(role?: string | null): ReactNode {
  if (!role) return <span style={muted}>—</span>;
  return <Badge tone="neutral">{roleLabel(role)}</Badge>;
}

/** Contract direction as a tinted `Badge` (earn=success/in, pay=warning/out). */
export function directionCell(direction?: string | null): ReactNode {
  if (!direction) return <span style={muted}>—</span>;
  const tone = direction === 'earn' ? 'success' : 'warning';
  return <Badge tone={tone}>{directionLabel(direction)}</Badge>;
}

/** `start – end` date range (nowrap), em-dash when both empty. */
export function dateRangeCell(start?: string | null, end?: string | null): ReactNode {
  if (!start && !end) return <span style={muted}>—</span>;
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      {formatDate(start)} – {formatDate(end)}
    </span>
  );
}

/** Right-aligned DD/MM/YYYY date cell. */
export function dateCell(iso?: string | null): ReactNode {
  return (
    <span style={{ display: 'block', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatDate(iso)}</span>
  );
}

/** Comma-joined string list (CS followers, POC notes), em-dash fallback. */
export function listCell(value?: string[] | null): ReactNode {
  const text = value?.filter(Boolean).join(', ');
  if (!text) return <span style={muted}>—</span>;
  return (
    <span style={nowrap} title={text}>
      {text}
    </span>
  );
}

/** Plain text with an em-dash fallback + ellipsis. */
export function textCell(value?: string | null): ReactNode {
  if (!value) return <span style={muted}>—</span>;
  return (
    <span style={nowrap} title={value}>
      {value}
    </span>
  );
}
